import {
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { DownloadIcon } from '@heroicons/react/solid';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  FundraisingEntityInfosFragment,
  useGenerateRmtExtractUrlMutation,
  useGenerateRmtUrlMutation,
  useGetCompanyShareTransferRegisterQuery,
  useGetFundraisingShareTransferRegisterQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import {
  AssetBadge,
  ErrorQueryCard,
  IdentityAvatar,
  TableContainer,
} from '@blockpulse3/ui/commons';

type Props = {
  fundraising?: FundraisingEntityInfosFragment | undefined;
};

/**
 * ShareTransferRegisterTable.
 * RMT table.
 *
 * @returns {JSX.Element}
 */
export function ShareTransferRegisterTable({ fundraising }: Props): JSX.Element {
  const i18nShareTransferRegister = useTranslations('CaptableShareTransferRegister');

  const { companyId = '' } = useParams();

  const { data, loading, error } = useGetCompanyShareTransferRegisterQuery({
    variables: { companyId: companyId || '' },
    skip: !companyId || !!fundraising?.id,
  });

  const {
    data: dataFundraising,
    loading: loadingFundraising,
    error: errorFundraising,
  } = useGetFundraisingShareTransferRegisterQuery({
    variables: { fundraisingId: fundraising?.id || '' },
    skip: !fundraising?.id,
  });

  const [generateRmtUrl, { loading: isRmtLoading }] = useGenerateRmtUrlMutation();
  const [generateRmtExtractUrl, { loading: isRmtExtractLoading }] =
    useGenerateRmtExtractUrlMutation();

  const handleDownloadRmt = (): void => {
    if (companyId) {
      generateRmtUrl({
        variables: { companyId },
        onCompleted: (data) => {
          window.open(data?.generateRmtUrl, '_blank');
        },
      });
    }
    if (fundraising?.id) {
      generateRmtExtractUrl({
        variables: { fundraisingId: fundraising?.id },
        onCompleted: (data) => {
          window.open(data?.generateRmtExtractUrl, '_blank');
        },
      });
    }
  };

  if (loading || loadingFundraising) {
    return (
      <Skeleton>
        <Card h="270px" />
      </Skeleton>
    );
  }

  if (error || errorFundraising) {
    return <ErrorQueryCard />;
  }

  const movements =
    data?.getCompanyShareTransferRegister ||
    dataFundraising?.getFundraisingShareTransferRegister ||
    [];

  return (
    <Card>
      <CardHeader
        as={Stack}
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
      >
        <Heading size="md">
          {fundraising ? fundraising.name + ' - ' : undefined}
          {i18nShareTransferRegister('Title')}
        </Heading>
        {(companyId || fundraising?.id) && movements.length > 0 && (
          <Button
            isLoading={isRmtLoading || isRmtExtractLoading}
            loadingText={i18nShareTransferRegister('ButtonLoading')}
            variant="secondary"
            width={{ base: 'fit-content' }}
            onClick={(): void => handleDownloadRmt()}
          >
            <HStack>
              <Text>{i18nShareTransferRegister('Export')}</Text>
              <Icon as={DownloadIcon} boxSize="5" color="secondary" />
            </HStack>
          </Button>
        )}
      </CardHeader>
      <CardBody>
        <TableContainer maxHeight="none">
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>{i18nShareTransferRegister('Index')}</Th>
                <Th>{i18nShareTransferRegister('Holder')}</Th>
                <Th>{i18nShareTransferRegister('Recipient')}</Th>
                <Th isNumeric>{i18nShareTransferRegister('Shares')}</Th>
                <Th textAlign="right">{i18nShareTransferRegister('MovementDate')}</Th>
                <Th textAlign="right">{i18nShareTransferRegister('MovementNature')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {movements.map((movement) => (
                <Tr key={movement.id}>
                  <Td>
                    <Text>{movement.number === 0 ? '-' : movement.number}</Text>
                  </Td>
                  <Td>
                    {movement.fromWallet?.identity ? (
                      <HStack spacing="2.5">
                        <IdentityAvatar identity={movement.fromWallet.identity} />
                        <Stack spacing="1">
                          <Text fontWeight="600">
                            {movement.fromWallet?.identity?.individualIdentity?.name ||
                              movement.fromWallet?.identity?.companyIdentity?.name}
                          </Text>
                          {movement.fromWalletIndex && (
                            <Text color="gray.500" fontWeight="400">
                              {movement.fromWalletIndex}
                            </Text>
                          )}
                        </Stack>
                      </HStack>
                    ) : (
                      <Text fontWeight="600" textAlign="left">
                        -
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {movement.toWallet?.identity ? (
                      <HStack spacing="2.5">
                        <IdentityAvatar identity={movement.toWallet.identity} />
                        <Stack spacing="1">
                          <Text fontWeight="600">
                            {movement.toWallet?.identity?.individualIdentity?.name ||
                              movement.toWallet?.identity?.companyIdentity?.name}
                          </Text>
                          {movement.toWalletIndex && (
                            <Text color="gray.500" fontWeight="400">
                              {movement.toWalletIndex}
                            </Text>
                          )}
                        </Stack>
                      </HStack>
                    ) : (
                      <Text fontWeight="600" textAlign="left">
                        -
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <AssetBadge type={movement.asset.type} value={movement.amount} />
                  </Td>
                  <Td textAlign="right">
                    <Text fontWeight="600">{formatDate(movement.createdAt, 'D MMMM YYYY')}</Text>
                  </Td>
                  <Td color="secondary" fontWeight="600" textAlign="right">
                    <Text fontWeight="600">
                      {i18nShareTransferRegister(movement?.type ?? 'INCORRECT_MOVEMENT_TYPE')}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
}
